import { consola } from 'consola'
import { computed, ref } from 'vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { 
  TIMER_URGENCY_THRESHOLDS, 
} from '@shared/config'
// ✅ Импортируем централизованные утилиты для работы с датами (DRY принцип)
import {
  convertToMoscowTime as convertToMoscowTimeUtil,
  isOrderRecent as isOrderRecentUtil,
  calculateOrderExpiration as calculateOrderExpirationUtil,
  logOrderTimeInfo as _logOrderTimeInfo,
  DATE_CONSTANTS,
} from '@shared/lib'

// Инициализируем плагины dayjs
dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Composable для работы с временем заказов
 * Следует принципу Single Responsibility - только время
 * ✅ Использует централизованные утилиты из utils/date/dateUtils.ts (DRY)
 */
export function useOrderTime() {
  const remainingTime = ref<number>(0)

  /**
   * ✅ Делегирует в централизованную утилиту
   * Преобразует UTC время в московское
   */
  function convertToMoscowTime(utcDate: string) {
    return convertToMoscowTimeUtil(utcDate).moscowTime
  }

  /**
   * ✅ Делегирует в централизованную утилиту с дополнительным логированием
   * Проверяет, является ли заказ свежим (создан в последние 15 минут)
   */
  function isOrderRecent(createdAt: string): boolean {
    const isRecent = isOrderRecentUtil(createdAt)
    
    // Детальное логирование для отладки
    const created = dayjs.utc(createdAt)
    const now = dayjs.utc()
    const fifteenMinutesAgo = now.subtract(DATE_CONSTANTS.PAYMENT_TIMEOUT_MINUTES, 'minutes')
    
    consola.debug('⏰ [isOrderRecent] Check:', {
      createdAt,
      createdUTC: created.toISOString(),
      createdMoscow: created.tz(DATE_CONSTANTS.MOSCOW_TIMEZONE).format('DD.MM.YYYY HH:mm:ss'),
      nowUTC: now.toISOString(),
      nowMoscow: now.tz(DATE_CONSTANTS.MOSCOW_TIMEZONE).format('DD.MM.YYYY HH:mm:ss'),
      fifteenMinutesAgoUTC: fifteenMinutesAgo.toISOString(),
      fifteenMinutesAgoMoscow: fifteenMinutesAgo.tz(DATE_CONSTANTS.MOSCOW_TIMEZONE).format('DD.MM.YYYY HH:mm:ss'),
      isRecent,
      ageMinutes: now.diff(created, 'minutes'),
    })
    
    return isRecent
  }

  /**
   * ✅ Делегирует в централизованную утилиту с расширенной информацией
   * Вычисляет оставшееся время до истечения заказа
   */
  function calculateOrderExpiration(createdAt: string) {
    const expiration = calculateOrderExpirationUtil(createdAt)
    return {
      ...expiration,
      moscowTime: convertToMoscowTime(createdAt),
    }
  }

  /**
   * Форматирует оставшееся время в MM:SS или Xч Xм
   */
  const formattedTimeRemaining = computed(() => {
    const time = remainingTime.value
    
    if (!time) return null
    
    const totalMinutes = Math.floor(time / 60_000)
    const seconds = Math.floor((time % 60_000) / 1000)
    
    // Если времени больше 60 минут, показываем в формате "Хч Хм"
    if (totalMinutes >= 60) {
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60
      return `${hours}ч ${minutes}м`
    }
    
    // Если времени меньше 60 минут, показываем в формате "ММ:СС"
    return `${totalMinutes}:${seconds.toString().padStart(2, '0')}`
  })

  /**
   * Определяет критичность времени для стилизации
   */
  function getTimerUrgency(createdAt: string): 'critical' | 'urgent' | '' {
    const expiration = calculateOrderExpiration(createdAt)
    const remainingMinutes = expiration.remainingMinutes
    
    if (remainingMinutes <= TIMER_URGENCY_THRESHOLDS.CRITICAL) return 'critical'
    if (remainingMinutes <= TIMER_URGENCY_THRESHOLDS.URGENT) return 'urgent'
    return ''
  }

  /**
   * Обновляет оставшееся время (вызывается таймером)
   */
  function updateRemainingTime(time: number): void {
    remainingTime.value = Math.max(0, time)
  }

  return {
    // State
    remainingTime: computed(() => remainingTime.value),
    formattedTimeRemaining,
    
    // Actions
    convertToMoscowTime,
    isOrderRecent,
    calculateOrderExpiration,
    getTimerUrgency,
    updateRemainingTime,
  }
}
